import { Fragment, useMemo } from 'react';
import type { PayRequestView } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { formatDay, formatRub, groupByDepartment, invoiceLabel, stageTone } from '../model';
import { requestsCount } from './text';
import {
  Amount,
  Flags,
  Group,
  GroupHead,
  GroupMeta,
  GroupTitle,
  PersonHead,
  RowLink,
  RowList,
  RowMeta,
  RowPurpose,
  RowSide,
  RowTitle,
  Small,
} from './styled';

/**
 * Заявка одной строкой: поставщик, счёт, назначение, сумма и где она сейчас.
 * Кто подал, не пишем: в общих списках сотрудник — заголовок группы.
 */
export function RequestRow({ r }: { r: PayRequestView }) {
  const invoice = invoiceLabel(r);
  const partlyPaid = r.paid_total > 0.005 && r.paid_total < r.amount - 0.005;
  return (
    <RowLink to={`/pay-requests/${r.id}`}>
      <div>
        <RowTitle>{r.counterparty_name}</RowTitle>
        <RowMeta>
          {r.request_number ?? 'Номер ещё не присвоен'} · подана {formatDay(r.created_at)}
          {invoice && ` · ${invoice}`}
        </RowMeta>
        <RowPurpose title={r.purpose}>{r.purpose}</RowPurpose>
        {r.decision_reason && (
          <RowMeta>
            {/* Отклонили часть, а заявка в остальном жива: не путать с отказом целиком. */}
            {r.stage === 'rejected' || r.stage === 'deferred'
              ? 'Причина'
              : 'Часть отклонена или отложена'}
            : {r.decision_reason}
          </RowMeta>
        )}
      </div>
      <RowSide>
        <Amount>{formatRub(r.amount)}</Amount>
        {partlyPaid && <Small>оплачено {formatRub(r.paid_total)}</Small>}
        <Flags>
          {r.urgent && <Badge $tone="danger">Срочно</Badge>}
          <Badge $tone={stageTone(r.stage)}>{r.stage_label}</Badge>
          {r.closing === 'awaiting' && <Badge $tone="warning">Нет закрывающих</Badge>}
          {r.closing === 'provided' && <Badge $tone="success">Закрывающие есть</Badge>}
        </Flags>
      </RowSide>
    </RowLink>
  );
}

interface GroupedProps {
  items: PayRequestView[];
  /** id отделов в порядке справочника «Службы». */
  order: string[];
}

/** Заявки по отделам, в отделе — по сотрудникам. */
export function GroupedRequests({ items, order }: GroupedProps) {
  const groups = useMemo(() => groupByDepartment(items, order), [items, order]);
  return (
    <>
      {groups.map((g) => (
        <Group key={g.key} aria-label={g.name}>
          <GroupHead>
            <GroupTitle>{g.name}</GroupTitle>
            <GroupMeta>
              {requestsCount(g.count)} · {formatRub(g.amount)}
            </GroupMeta>
          </GroupHead>
          {g.people.map((p) => (
            <Fragment key={p.key}>
              <PersonHead>
                {p.name}
                <GroupMeta>
                  {requestsCount(p.items.length)} · {formatRub(p.amount)}
                </GroupMeta>
              </PersonHead>
              <RowList>
                {p.items.map((r) => (
                  <RequestRow key={r.id} r={r} />
                ))}
              </RowList>
            </Fragment>
          ))}
        </Group>
      ))}
    </>
  );
}
